

































































































import { defineComponent, PropType, computed } from '@nuxtjs/composition-api';
import useWindowResize from '../../hooks/useResizeWindow';
import usePromotionsDrawer from '~/hooks/usePromotionsDrawer';

type ImageGalleryContentVariant = 'image' | 'youtube' | 'vimeo';
export type ImageGalleryContent = Array<{
  type: ImageGalleryContentVariant;
  media_url?: string;
  url_safe_name: string;
}>;

type Promotion = {
  action_label: string;
  is_blackfriday_deal: boolean;
  category: string;
  variant: string;
  short_description: string;
  long_description: string;
  external_url: string;
};

export default defineComponent({
  props: {
    content: {
      type: Array as PropType<ImageGalleryContent>,
      default: () => [
        {
          type: 'image',
          media_url: '',
          url_safe_name: ''
        }
      ]
    },
    promotions: {
      type: Array as PropType<Promotion[]>,
      default: () => []
    },
    product: {
      type: Object,
      default: () => {}
    },
    title: { type: String, default: '__' }
  },
  data() {
    return {
      current: 0,
      currentImageIndex: 1,
      isZoomed: false,
      supportedMediaTypes: ['image', 'youtube', 'vimeo']
    };
  },
  computed: {
    listFormattedImages() {
      return this.content.map(
        (item: { type: string; media_url: string; url_safe_name: string }) => ({
          desktop: {
            url: item.media_url
          },
          mobile: {
            url: item.media_url
          },
          zoom: {
            url: item.media_url
          },
          placeholder: item.media_url,
          alt: this.title
        })
      );
    },
    listFormattedContent() {
      return this.content
        .filter((image) => this.supportedMediaTypes.includes(image.type))
        .map(
          (image: {
            type: ImageGalleryContentVariant;
            media_url: string;
            media_file_name: string;
            url_safe_name: string;
          }) => {
            if (image.type === 'image')
              return {
                type: image.type,
                desktop: {
                  url: image.media_url
                },
                mobile: {
                  url: image.media_url
                },
                zoom: {
                  url: image.media_url
                },
                placeholder: image.media_url,
                alt: image.media_file_name
              };
            if (image.type === 'youtube') {
              const videoCode = image.media_url;
              const url = `https://www.youtube.com/embed/${videoCode}`;
              return {
                type: image.type,
                isMobile: !this.isDesktop,
                desktop: {
                  height: 400,
                  width: 711,
                  url
                },
                mobile: {
                  height: 140,
                  width: 248,
                  url
                },
                zoom: {
                  url
                },
                thumbnail: `https://img.youtube.com/vi/${videoCode}/maxresdefault.jpg`,
                placeholder: url,
                alt: this.type
              };
            }
            if (image.type === 'vimeo') {
              const videoCode = image.media_url;
              const url = `https://player.vimeo.com/video/${videoCode}`;
              return {
                type: image.type,
                isMobile: !this.isDesktop,
                desktop: {
                  height: 400,
                  width: 711,
                  url
                },
                mobile: {
                  height: 140,
                  width: 248,
                  url
                },
                zoom: {
                  url
                },
                thumbnail: `https://vumbnail.com/${videoCode}.jpg`,
                placeholder: url,
                alt: this.type
              };
            }
          }
        );
    },
    listZoomImages() {
      return this.listFormattedContent.filter((item) => item.type === 'image');
    },
    numberOfImages() {
      return this.content.length;
    },
    numberOfZoomImages() {
      return this.listZoomImages.length;
    },
    hasPromotions() {
      return this.promotions.length > 0;
    },
    numberOfPromotions() {
      return this.promotions.length;
    }
  },
  methods: {
    handleStepperClick(clickedStep) {
      this.current = clickedStep;
      this.currentImageIndex = clickedStep + 1;
      this.$children[0].go(clickedStep);
      return this.current;
    },
    zoomOnImage(clickedImage) {
      let removeImagesToIndex = 0;
      for (let i = 0; i < clickedImage.index; i++) {
        removeImagesToIndex += this.content[i].type !== 'image' ? 1 : 0;
      }
      this.isZoomed = true;
      this.current = Math.min(
        clickedImage.index - removeImagesToIndex,
        this.numberOfZoomImages - 1
      );
      this.currentImageIndex = this.current + 1;
    },
    changeCurrentBasedOnSwipe(newIndex: number) {
      this.current = newIndex - 1;
    },
    handleCloseModal() {
      let i = 0;
      let addImagesToIndex = 0;
      while (i <= this.current + addImagesToIndex) {
        if (this.content[i].type !== 'image') addImagesToIndex++;
        i++;
      }
      this.current = Math.min(
        this.current + addImagesToIndex,
        this.numberOfImages - 1
      );
      this.$children[0].go(
        Math.min(this.current, this.listFormattedContent.length - 1)
      );
      this.isZoomed = false;
    },
    handlePreviousClick() {
      let nextSlide = this.current - 1;
      if (nextSlide < 0) nextSlide = this.listFormattedContent.length - 1;
      this.$children[0].go(nextSlide);
    },
    handleNextClick() {
      let nextSlide = this.current + 1;
      if (nextSlide >= this.listFormattedContent.length) nextSlide = 0;
      this.$children[0].go(nextSlide);
    },
    handlePreviousClickZoomed() {
      let nextSlide = this.current - 1;
      if (nextSlide < 0) nextSlide = this.listZoomImages.length - 1;
      this.current = nextSlide;
      this.$refs.zoomed.children[0].children[0].__vue__.go(nextSlide);
    },
    handleNextClickZoomed() {
      let nextSlide = this.current + 1;
      if (nextSlide >= this.listZoomImages.length) nextSlide = 0;
      this.current = nextSlide;
      this.$refs.zoomed.children[0].children[0].__vue__.go(nextSlide);
    },
    scrollCurrentThumbnailToCenter() {
      this.$nextTick(() => {
        const thumbnailsContainers = document.getElementsByClassName(
          'sf-gallery__thumbs'
        ) as HTMLCollectionOf<HTMLElement>;
        for (let i = 0; i < thumbnailsContainers.length; i++) {
          const thumbnailsContainer = thumbnailsContainers[i];
          const currentThumbnail = thumbnailsContainer.getElementsByClassName(
            'sf-gallery__item--selected'
          )?.[0] as HTMLElement;
          if (!currentThumbnail) continue;

          /* prettier-ignore */
          const containerLeftScroll = currentThumbnail.offsetLeft -
            (thumbnailsContainer.offsetWidth / 2) +
            (currentThumbnail.offsetWidth / 2);

          thumbnailsContainer.scrollTo({
            left: containerLeftScroll,
            behavior: 'smooth'
          });
        }
      });
    }
  },
  watch: {
    current() {
      this.scrollCurrentThumbnailToCenter();
    }
  },
  setup(props) {
    const { isDesktop } = useWindowResize();
    const { openPromotionsDrawer } = usePromotionsDrawer();

    const formattedPromotions = computed(() => {
      const blackFridayLabels =
        props.promotions
          ?.filter((label) => label.action_label && label.is_blackfriday_deal)
          .map((label) => ({
            ...label,
            variant: 'black-friday'
          })) || [];

      const awardLabels =
        props.promotions
          ?.filter(
            (label) =>
              label.action_label &&
              !label.is_blackfriday_deal &&
              label.category === 'Product Awards'
          )
          .map((label) => ({
            ...label,
            variant: 'award'
          })) || [];

      const promotionLabels =
        props.promotions
          ?.filter(
            (label) =>
              label.action_label &&
              !label.is_blackfriday_deal &&
              label.category !== 'Product Awards'
          )
          .map((label) => ({
            ...label,
            variant: 'promotion'
          })) || [];

      return [...blackFridayLabels, ...promotionLabels, ...awardLabels];
    });

    const handlePromotionClick = (promotion) => {
      openPromotionsDrawer(promotion);
    };

    return {
      isDesktop,
      formattedPromotions,
      handlePromotionClick
    };
  }
});
